import React, { useState, useEffect } from 'react';

import _ from 'lodash';
import { Formik, Form, useFormikContext } from 'formik';

import useMenus from '../../context/MenuContext';
import Yup from '../../utils/yup';
import { PropertyType } from '../../config/property';
import masterDataService from '../../service/masterData.service';
import propertyService from '../../service/property.service';
import { NotificationType } from '../../config';
import addNotification from '../../utils/addNotification';

import FormFields from './FormFields';
import { FormHeaderUI, FormButtonPanelUI, DeleteDialogUI, FormBase } from '../Interface';
import PropertyReports from './PropertyReports';
import FormsDataGrid from './FormsDataGrid';
import SelectListDatagrid from './SelectListDatagrid';
import GroupsDataGrid from './GroupsDataGrid';

export const PropertyForm = () => {
  const { menus, getMenus, getMenuSubMenuId, getCanEdit } = useMenus();
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [editButtonVisible, setEditButtonvisible] = useState(true);
  const [addField, setAddField] = useState(true);
  const [initValues, setInitValues] = useState({});
  const [refetchTable, setRefetchTable] = useState(false);
  const [viewField, setViewField] = useState(true);
  const [selectedValues, setSelectedValues] = useState({});
  const [operatorsDisabled, setOperatorsDisabled] = useState(false);
  const [showOptionDataGrid, setShowOptionDataGrid] = useState(false);
  const [formProperty, setFormProperty] = useState([]);
  const [canEdit, setCanEdit] = useState(true);
  const [defaultValues, setDefaultValues] = useState({});
  const [rowSelectionModel, setRowSelectionModel] = useState([]);
  const [formPropertyGroup, setFormPropertyGroup] = useState([]);

  const defPropertyOption = {
    name: '',
    color: '#FFFFFF',
    isNew: true,
  };

  const def = {
    name: '',
    mandatory: false,
    type: '',
    visOrder: '',
    characterLength: '',
    expiryMonitoring: false,
    expiryAlertDays: null,
    emailNotification: false,
    propertyOption: [],
    formProperty: [],
    formPropertyGroup: [],
  };

  useEffect(() => {
    getMenus();
    getFormTypeSubmenus();
    setDefaultValues(def);
  }, []);

  useEffect(() => {
    const submenuId = getMenuSubMenuId('properties');
    setCanEdit(getCanEdit(submenuId));
  }, [menus]);

  const validationSchema = Yup.object().shape({
    name: Yup.string().required(),
    mandatory: Yup.string(),
    type: Yup.string().required(),
    visOrder: Yup.number().nullable().positive().required(),
    characterLength: Yup.number()
      .nullable()
      .positive()
      .max(329)
      .test('characterLength-required', 'Kötelező', function (value) {
        const { type } = this.parent;
        if (type === PropertyType.LONGTEXT) {
          return value !== null && value !== undefined;
        }
        return true;
      }),
    expiryMonitoring: Yup.boolean(),
    expiryAlertDays: Yup.number()
      .nullable()
      .positive()
      .test('expiryAlertDays-required', 'Kötelező', function (value) {
        const { expiryMonitoring } = this.parent;
        if (expiryMonitoring) {
          return value !== null && value !== undefined;
        }
        return true;
      }),
    emailNotification: Yup.boolean(),
    propertyOption: Yup.array().of(
      Yup.object().shape({
        name: Yup.string().required(),
        color: Yup.string()
          .nullable()
          .matches(/^$|^#(?:[0-9a-fA-F]{3}){1,2}$/)
          .max(7),
      })
    ),
  });

  const handleSubmit = (values) => {
    if (!editButtonVisible) {
      updateObject(values);
    } else if (!addField) {
      handleInsertObject(values);
    }
  };

  useEffect(() => {
    if (Object.keys(selectedValues).length > 0) {
      if (!_.isEqual(selectedValues)) {
        valuesToForm();
        setOperatorsDisabled(false);
      } else {
        setOperatorsDisabled(true);
      }
    } else {
      setInitValues(defaultValues);
      setOperatorsDisabled(true);
    }
  }, [selectedValues]);

  const valuesToForm = () => {
    const {
      name,
      mandatory,
      type,
      visOrder,
      formProperty: formValues,
      formPropertyGroup: formGroupValues,
      propertyOption,
      characterLength,
      expiryMonitoring,
      expiryAlertDays,
      emailNotification,
    } = selectedValues;

    const updatedFormProperty = formProperty.map((submenu) => ({
      ...submenu,
      checked: formValues?.some((form) => form.formId === submenu.subMenuId) || false,
    }));

    setInitValues({
      name,
      mandatory,
      type,
      visOrder,
      characterLength: characterLength ?? defaultValues.characterLength,
      expiryMonitoring,
      expiryAlertDays,
      emailNotification,
      formProperty: formValues?.map((form) => form.formId),
      formPropertyGroup: formGroupValues?.map((group) => ({
        formId: group.formId,
        groupId: group.groupId,
      })),
      propertyOption: propertyOption.map((option) => ({
        id: option.id,
        name: option.name,
        color: option.color,
      })),
    });
    setFormProperty(updatedFormProperty);
  };

  const getFormTypeSubmenus = () => {
    masterDataService.formTypeSubmenus().then((data) => {
      setFormProperty(data);
    });
  };

  const getPropertyGroupsForSubmenus = async () => {
    if (selectedValues && selectedValues.formProperty && Array.isArray(selectedValues.formProperty)) {
      const formIds = selectedValues.formProperty.map((property) => property.formId);

      if (formIds.length > 0) {
        const data = await propertyService.getPropertyGroupsForSubmenus({ data: formIds });

        const updatedFormPropertyGroup = data.map((submenu) => ({
          ...submenu,
          checked: selectedValues.formPropertyGroup?.some((form) => form.groupId === submenu.id) || false,
        }));

        setFormPropertyGroup(updatedFormPropertyGroup);
      }
    }
  };

  useEffect(() => {
    if (!selectedValues.formProperty?.length) {
      setFormPropertyGroup([]);
    }
  }, [selectedValues.formProperty]);

  useEffect(() => {
    if (selectedValues && selectedValues.formProperty) {
      getPropertyGroupsForSubmenus();
    }
  }, [selectedValues, selectedValues.formProperty]);

  const updateObject = (values) => {
    const updData = {
      ...values,
      characterLength: parseInt(values.characterLength),
      expiryAlertDays: parseInt(values.expiryAlertDays),
    };

    propertyService.updateProperty(updData, selectedValues.id).then((_simpleTask) => {
      addNotification({
        content: 'Sikeres módosítás!',
        type: NotificationType.SUCCESS,
      });

      setRefetchTable(true);
      setEditButtonvisible(true);
      setViewField(true);
    });
  };

  const handleInsertObject = (values) => {
    const insertObject = {
      ...values,
      characterLength: parseInt(values.characterLength),
      expiryAlertDays: parseInt(values.expiryAlertDays),
    };

    propertyService
      .createProperty(insertObject)
      .then((data) => {
        addNotification({
          content: 'Sikeres mentés!',
          type: NotificationType.SUCCESS,
        });
        setRefetchTable(true);
        setAddField(true);
        setViewField(true);
        setSelectedValues(data);

        setRowSelectionModel(data.id);
      })

      .finally(() => {
        //@TODO loader
      });
  };

  const handleRemoveElement = async () => {
    propertyService.deleteProperty(selectedValues.id).then(() => {
      addNotification({
        content: 'Sikeres törlés!',
        type: NotificationType.WARNING,
      });
      setRefetchTable(true);
      setShowDeleteDialog(false);
      setSelectedValues(defaultValues);
    });
  };

  const ShowOptionGrid = () => {
    const { values } = useFormikContext();
    useEffect(() => {
      if (values.type === PropertyType.COMBO) {
        setShowOptionDataGrid(true);
      } else {
        setShowOptionDataGrid(false);
      }
    }, [values.type]);
    return null;
  };

  return (
    <>
      <FormBase
        name="Property"
        leftPanelContent={
          <div className="h-full overflow-auto custom-scrollbar">
            <FormHeaderUI
              addClick={() => {
                setSelectedValues(defaultValues);
                setSelectedValues({});
                setViewField(false);
                setAddField(false);
              }}
              addDisabled={!viewField || !canEdit}
              modClick={() => {
                setViewField(false);
                setEditButtonvisible(false);
              }}
              modDisabled={!viewField || operatorsDisabled || !canEdit}
              deleteClick={() => {
                setShowDeleteDialog(true);
              }}
              deleteDisabled={!viewField || operatorsDisabled || !canEdit}
              copyClick={() => {
                setViewField(false);
                setAddField(false);
              }}
              copyDisabled={!viewField || operatorsDisabled || !canEdit}
            />

            <Formik
              initialValues={initValues}
              validationSchema={validationSchema}
              validateOnChange={false}
              onSubmit={handleSubmit}
              enableReinitialize
            >
              {({ values, setFieldValue, errors, validateField, resetForm }) => (
                <Form>
                  <ShowOptionGrid />
                  <FormFields
                    viewField={viewField}
                    values={values}
                    setFieldValue={setFieldValue}
                    errors={errors}
                    validateField={validateField}
                    selectedValues={selectedValues}
                  />

                  <div className="grid grid-cols-5 gap-6 p-2">
                    <div className="col-span-2">
                      <div>
                        <FormsDataGrid
                          formProperty={formProperty}
                          disabled={viewField}
                          setFormPropertyGroup={setFormPropertyGroup}
                        />
                        <div className="mt-2">
                          <GroupsDataGrid formPropertyGroup={formPropertyGroup} disabled={viewField} />
                        </div>
                      </div>
                    </div>
                    {showOptionDataGrid && (
                      <div className="col-span-3">
                        <SelectListDatagrid disabled={viewField} defPropertyOption={defPropertyOption} />
                      </div>
                    )}
                  </div>

                  <FormButtonPanelUI
                    onCancelClick={() => {
                      setViewField(true);
                      setEditButtonvisible(true);
                      setAddField(true);
                      resetForm();
                    }}
                    disabled={editButtonVisible && addField}
                  />
                </Form>
              )}
            </Formik>
          </div>
        }
        rightPanelContent={
          <PropertyReports
            refetch={{ refetchTable, setRefetchTable }}
            values={{ selectedValues, setSelectedValues }}
            viewField={viewField}
            operatorsDisabled={operatorsDisabled}
            rowSelection={{ rowSelectionModel, setRowSelectionModel }}
          />
        }
      />
      <DeleteDialogUI
        handleRemoveElement={handleRemoveElement}
        show={showDeleteDialog}
        onHide={() => setShowDeleteDialog(false)}
      />
    </>
  );
};
